import { useState } from 'react';
import {
  Plus,
  DollarSign,
  MoreHorizontal,
  Trash2,
  Pencil,
  Building2,
  User,
  Calendar,
  Download,
  Upload,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';
import { usePipelines, usePipelineSummary } from '@/lib/hooks/crm/usePipelines';
import { useCompanies } from '@/lib/hooks/crm/useCompanies';
import { useContacts } from '@/lib/hooks/crm/useContacts';
import api from '@/lib/api';
import type { CrmDeal, CrmCreateDealInput } from '@/lib/api';
import { CustomFieldEditor } from './CustomFieldEditor';
import { TimelinePanel } from './TimelinePanel';
import { ImportDialog } from './ImportDialog';
import { useCrmAI } from '@/lib/hooks/crm/useAI';
import { useImportExport } from '@/lib/hooks/crm/useImportExport';

function formatCurrency(value: number, currency: string = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

const emptyForm: CrmCreateDealInput = {
  pipelineId: '',
  stageId: '',
  name: '',
  value: 0,
  currency: 'USD',
  expectedCloseDate: '',
  notes: '',
  tags: [],
};

export function DealsView() {
  const { pipelines, loading: pipelinesLoading } = usePipelines();
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(null);

  // Auto-select default pipeline
  const activePipelineId =
    selectedPipelineId ?? pipelines.find((p) => p.isDefault)?.id ?? pipelines[0]?.id ?? null;

  const { summary, loading: summaryLoading, refetch } = usePipelineSummary(activePipelineId);
  const { companies } = useCompanies({ limit: 200 });
  const { contacts } = useContacts({ limit: 200 });
  const { predictWinLikelihood, loading: aiLoading } = useCrmAI();

  const [showForm, setShowForm] = useState(false);
  const [editingDeal, setEditingDeal] = useState<CrmDeal | null>(null);
  const [form, setForm] = useState<CrmCreateDealInput>({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [draggedDealId, setDraggedDealId] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);
  const { exportEntities, exporting } = useImportExport();

  function openCreate(stageId?: string) {
    if (!activePipelineId) return;
    setEditingDeal(null);
    setForm({
      ...emptyForm,
      pipelineId: activePipelineId,
      stageId: stageId ?? summary?.stages[0]?.stageId ?? '',
    });
    setShowForm(true);
  }

  function openEdit(deal: CrmDeal) {
    setEditingDeal(deal);
    setForm({
      pipelineId: deal.pipelineId,
      stageId: deal.stageId,
      contactId: deal.contactId ?? undefined,
      companyId: deal.companyId ?? undefined,
      name: deal.name,
      value: deal.value,
      currency: deal.currency,
      expectedCloseDate: deal.expectedCloseDate ?? '',
      notes: deal.notes ?? '',
      tags: deal.tags,
      metadata: deal.metadata ?? {},
    });
    setShowForm(true);
  }

  async function handleSave() {
    if (!form.name.trim()) {
      toast({ title: 'Validation', description: 'Deal name is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (editingDeal) {
        await api.updateCrmDeal(editingDeal.id, form);
        toast({ title: 'Deal updated' });
      } else {
        await api.createCrmDeal(form);
        toast({ title: 'Deal created' });
      }
      setShowForm(false);
      refetch();
    } catch {
      toast({ title: 'Error', description: 'Failed to save deal', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(deal: CrmDeal) {
    try {
      await api.deleteCrmDeal(deal.id);
      toast({ title: 'Deal deleted' });
      refetch();
    } catch {
      toast({ title: 'Error', description: 'Failed to delete deal', variant: 'destructive' });
    }
  }

  // Drag and drop handlers
  function handleDragStart(e: React.DragEvent, dealId: string) {
    setDraggedDealId(dealId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', dealId);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }

  async function handleDrop(e: React.DragEvent, stageId: string) {
    e.preventDefault();
    const dealId = e.dataTransfer.getData('text/plain');
    setDraggedDealId(null);

    if (!dealId) return;

    try {
      await api.moveCrmDealToStage(dealId, stageId);
      refetch();
    } catch {
      toast({ title: 'Error', description: 'Failed to move deal', variant: 'destructive' });
    }
  }

  const loading = pipelinesLoading || summaryLoading;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="flex items-center gap-3">
          {pipelines.length > 1 && (
            <Select value={activePipelineId ?? ''} onValueChange={(v) => setSelectedPipelineId(v)}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select pipeline" />
              </SelectTrigger>
              <SelectContent>
                {pipelines.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                    {p.isDefault && ' (Default)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {summary && (
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>{summary.totalDeals} deals</span>
              <span>{formatCurrency(summary.totalValue)}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportEntities('deals')}
            disabled={exporting}
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowImport(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Import
          </Button>
          <Button onClick={() => openCreate()} disabled={!activePipelineId}>
            <Plus className="h-4 w-4 mr-2" />
            Add Deal
          </Button>
        </div>
      </div>

      {/* Kanban Board */}
      {loading ? (
        <div className="flex gap-4 p-6 overflow-x-auto flex-1">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-full w-72 min-w-72 flex-shrink-0" />
          ))}
        </div>
      ) : !summary || summary.stages.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground flex-1">
          <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-medium">No pipeline configured</p>
          <p className="text-sm mt-1">Create a pipeline to start tracking deals</p>
        </div>
      ) : (
        <div className="flex gap-4 p-6 overflow-x-auto flex-1 items-start">
          {summary.stages.map((stage) => (
            <div
              key={stage.stageId}
              className="w-72 min-w-72 flex-shrink-0 flex flex-col rounded-lg border bg-muted/30"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, stage.stageId)}
            >
              {/* Stage Header */}
              <div className="p-3 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: stage.stageColor }}
                    />
                    <span className="font-medium text-sm">{stage.stageName}</span>
                    <Badge variant="secondary" className="text-xs">
                      {stage.dealCount}
                    </Badge>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatCurrency(stage.totalValue)}
                  </span>
                </div>
              </div>

              {/* Deal Cards */}
              <div className="p-2 flex flex-col gap-2 min-h-[100px]">
                {stage.deals.map((deal) => (
                  <Card
                    key={deal.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, deal.id)}
                    onDragEnd={() => setDraggedDealId(null)}
                    className={`cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow ${
                      draggedDealId === deal.id ? 'opacity-50' : ''
                    }`}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between gap-1">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{deal.name}</p>
                          <p className="text-sm font-semibold text-primary mt-0.5">
                            {formatCurrency(deal.value, deal.currency)}
                          </p>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-6 w-6 -mr-1 -mt-1">
                              <MoreHorizontal className="h-3.5 w-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(deal)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => predictWinLikelihood(deal.id)}
                              disabled={aiLoading}
                            >
                              <Sparkles className="h-4 w-4 mr-2" />
                              Predict Win %
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDelete(deal)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                        {deal.companyName && (
                          <span className="flex items-center gap-1 truncate">
                            <Building2 className="h-3 w-3 flex-shrink-0" />
                            {deal.companyName}
                          </span>
                        )}
                        {deal.contactName && (
                          <span className="flex items-center gap-1 truncate">
                            <User className="h-3 w-3 flex-shrink-0" />
                            {deal.contactName}
                          </span>
                        )}
                      </div>

                      {deal.expectedCloseDate && (
                        <div className="flex items-center gap-1 mt-1.5 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          {new Date(deal.expectedCloseDate).toLocaleDateString()}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}

                {/* Add deal to stage button */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-muted-foreground hover:text-foreground"
                  onClick={() => openCreate(stage.stageId)}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Add deal
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className={editingDeal ? 'sm:max-w-3xl' : 'sm:max-w-lg'}>
          <DialogHeader>
            <DialogTitle>{editingDeal ? 'Edit Deal' : 'Add Deal'}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid gap-2">
              <Label htmlFor="deal-name">Name *</Label>
              <Input
                id="deal-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Enterprise License Deal"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="deal-value">Value</Label>
                <Input
                  id="deal-value"
                  type="number"
                  value={form.value ?? 0}
                  onChange={(e) => setForm({ ...form, value: Number(e.target.value) })}
                />
              </div>
              <div className="grid gap-2">
                <Label>Stage</Label>
                <Select
                  value={form.stageId}
                  onValueChange={(v) => setForm({ ...form, stageId: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {summary?.stages.map((s) => (
                      <SelectItem key={s.stageId} value={s.stageId}>
                        {s.stageName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Company</Label>
                <Select
                  value={form.companyId ?? ''}
                  onValueChange={(v) => setForm({ ...form, companyId: v || undefined })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Contact</Label>
                <Select
                  value={form.contactId ?? ''}
                  onValueChange={(v) => setForm({ ...form, contactId: v || undefined })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select contact" />
                  </SelectTrigger>
                  <SelectContent>
                    {contacts.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="deal-close-date">Expected Close Date</Label>
              <Input
                id="deal-close-date"
                type="date"
                value={form.expectedCloseDate ?? ''}
                onChange={(e) => setForm({ ...form, expectedCloseDate: e.target.value })}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="deal-notes">Notes</Label>
              <Textarea
                id="deal-notes"
                value={form.notes ?? ''}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>

          <CustomFieldEditor
            entityType="deal"
            metadata={form.metadata ?? {}}
            onChange={(metadata) => setForm({ ...form, metadata })}
          />

          {editingDeal && (
            <div className="border-t pt-4 mt-2">
              <TimelinePanel dealId={editingDeal.id} limit={10} />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editingDeal ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportDialog
        open={showImport}
        onOpenChange={setShowImport}
        entityType="deals"
        onImportComplete={() => {
          setShowImport(false);
          refetch();
        }}
      />
    </div>
  );
}
